import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { getOrCreateSubmission, getSubmission, linkSubmissionCase } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { resolveTaxFormId } from "@/features/workflows/api";
import { completeWorkflow, startOrResumeWorkflow } from "@/features/workflows/api";
import type { ResolutionWorkflow } from "@/features/workflows/types";
import { SpouseReliefWizard } from "@/features/workflows/spouse-relief/spouse-relief-wizard";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

/**
 * Case-scoped Spouse-Relief workflow
 * (`/client/:id/cases/:caseId/workflow/spouse-relief`): resolves the client's one
 * Form 8857 submission, links it to the case (so Part IV reads the case's shared
 * asset store), and records progress on a `ResolutionWorkflow` (type
 * SPOUSE_RELIEF). Archiving the PDF (submission → COMPLETED) marks the workflow
 * complete + writes a Case system-note. Honors `?part=1…7`.
 */
export default function ClientCaseSpouseReliefWorkflowPage() {
  const { client } = useActiveClient();
  const { caseId = "" } = useParams<{ caseId: string }>();
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const workflowRef = useRef<ResolutionWorkflow | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    (async () => {
      const taxFormId = await resolveTaxFormId("8857");
      let sub = await getOrCreateSubmission(client.id, taxFormId);
      // Attach the one client 8857 record to this case so its assets fill.
      if (sub.case !== caseId) sub = await linkSubmissionCase(sub.id, caseId);
      sub = await getSubmission(sub.id);
      workflowRef.current = await startOrResumeWorkflow(caseId, "SPOUSE_RELIEF");
      return sub;
    })()
      .then((sub) => {
        if (mountedRef.current) setSubmission(sub);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof ApiError ? err.message : "Couldn't open this workflow.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [client.id, caseId]);

  useEffect(load, [load]);

  // When the PDF is archived (submission → COMPLETED), complete the workflow once
  // (marks it COMPLETED + writes a Case system-note; no silent case-status change).
  const onSubmissionChange = useCallback(
    (next: FormSubmission) => {
      setSubmission(next);
      const wf = workflowRef.current;
      if (next.status === "COMPLETED" && wf && wf.status !== "COMPLETED") {
        completeWorkflow(caseId, wf.id)
          .then((updated) => {
            workflowRef.current = updated;
          })
          .catch(() => {
            /* best-effort progress; archiving already succeeded */
          });
      }
    },
    [caseId],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/client/${client.id}/cases/${caseId}`}>
            <ArrowLeft /> Back to Case
          </Link>
        </Button>
        <h2 className="text-foreground text-2xl font-bold">Spouse Relief (Form 8857)</h2>
      </div>
      <p className="text-muted-foreground -mt-2 text-sm">
        Guided Form 8857 (Request for Innocent Spouse Relief) questionnaire for {client.name}.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error || !submission ? (
        <p className="text-destructive py-8 text-sm">{error ?? "Couldn't open this workflow."}</p>
      ) : (
        <SpouseReliefWizard
          client={client}
          submission={submission}
          onSubmissionChange={onSubmissionChange}
          caseId={caseId}
        />
      )}
    </div>
  );
}
